// api/index.js
import axios from "axios";
import { monitor } from "../monitor";

function getBaseUrl() {
  const isDevelopment = process.env.REACT_APP_ENV === "development";
  return isDevelopment
    ? process.env.REACT_APP_API_URL_DEV
    : process.env.REACT_APP_API_URL_PROD;
}

function getAuthState() {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return {};
    return JSON.parse(raw)?.state ?? {};
  } catch {
    return {};
  }
}

function setAuthState(updates) {
  try {
    const raw = localStorage.getItem("auth-storage");
    const parsed = raw ? JSON.parse(raw) : { state: {} };
    parsed.state = { ...parsed.state, ...updates };
    localStorage.setItem("auth-storage", JSON.stringify(parsed));
  } catch {
    // ignore
  }
}

function clearAuthState() {
  try {
    localStorage.removeItem("auth-storage");
  } catch {
    // ignore
  }
}

function getUserTypeFromSessionStorage() {
  return getAuthState()?.user?.userType ?? null;
}

function assertStaffApiAllowed({ path }) {
  const userType = getUserTypeFromSessionStorage();
  if (userType !== "staff") return;

  // /courses(읽기)는 고객 후기 작성 에디터의 강의 선택 목록과
  // 후기 목록의 분류(강의로 유도) 표시에 필요하다.
  const staffAllowedPrefixes = ["/columns", "/reviews", "/courses"];
  const staffAllowedExact = ["/users/logout", "/users/login", "/users/refresh"];
  const isAllowed =
    typeof path === "string" &&
    (staffAllowedExact.includes(path) ||
      staffAllowedPrefixes.some((prefix) => path.startsWith(prefix)));
  if (!isAllowed) {
    const err = new Error("스태프 계정은 칼럼 관리 기능만 사용할 수 있습니다.");
    err.code = "STAFF_FORBIDDEN";
    throw err;
  }
}

// 동시에 여러 요청이 401을 받아도 refresh를 한 번만 시도하기 위한 상태
let isRefreshing = false;
let pendingQueue = []; // { resolve, reject }[]

function processPendingQueue(error, newToken = null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
  pendingQueue = [];
}

// 429 알림 디바운스 (연속 호출 시 중복 alert 방지)
let lastRateLimitAlertAt = 0;
function notifyRateLimited(error) {
  const now = Date.now();
  if (now - lastRateLimitAlertAt < 3000) return;
  lastRateLimitAlertAt = now;
  const serverMsg =
    error?.response?.data?.error ||
    error?.response?.data?.detail ||
    "";
  alert(
    `요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.${
      serverMsg ? `\n(${serverMsg})` : ""
    }`
  );
}

// 모니터링 응답 인터셉터를 auth 인터셉터보다 "먼저" 등록한다.
// axios 의 response 인터셉터는 등록 순서대로 실행되므로, 이렇게 해야
// 실패(401 등) 이벤트가 auth 의 하드 리다이렉트 전에 큐에 담긴다.
// (reportHttp/toMonitorPath 는 함수 선언이라 호이스팅되어 아래에서 정의돼도 참조된다.)
axios.interceptors.response.use(
  (response) => {
    reportHttp(response.config, response.status);
    return response;
  },
  (error) => {
    reportHttp(error.config, error.response?.status);
    return Promise.reject(error);
  }
);

// axios 인터셉터 설정 (auth: 위 모니터링 인터셉터 뒤에 등록)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      notifyRateLimited(error);
      return Promise.reject(error);
    }

    // refresh 요청 자체가 401이면 세션 만료 → 로그아웃
    if (
      error.response?.status === 401 &&
      originalRequest.url?.includes("/users/refresh")
    ) {
      clearAuthState();
      monitor.flush(); // 하드 리다이렉트 전 모니터링 큐 전송(유실 방지)
      window.location.href = "/signin";
      return Promise.reject(error);
    }

    // 401이고 재시도 전인 경우에만 refresh 시도
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken } = getAuthState();
      if (!refreshToken) {
        clearAuthState();
        monitor.flush(); // 하드 리다이렉트 전 모니터링 큐 전송(유실 방지)
        window.location.href = "/signin";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // 이미 refresh 중이면 대기열에 추가
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const baseUrl = getBaseUrl();
        const res = await axios.post(
          `${baseUrl}/users/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              "content-type": "application/json;charset=UTF-8",
              accept: "application/json",
            },
            withCredentials: true,
          }
        );

        const { access_token, refresh_token } = res.data;
        setAuthState({
          accessToken: access_token,
          refreshToken: refresh_token ?? refreshToken,
        });

        processPendingQueue(null, access_token);
        originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processPendingQueue(refreshError);
        clearAuthState();
        alert("로그인 세션이 만료되었습니다. 다시 로그인해주세요.");
        monitor.flush(); // 하드 리다이렉트 전 모니터링 큐 전송(유실 방지)
        window.location.href = "/signin";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── 모니터링 헬퍼 + 요청 타이머 ───────────────────────────────
// 모든 백엔드 API 호출(성공/실패)의 method/url/status/duration을 수집한다.
// 응답 인터셉터는 위에서 auth 보다 먼저 등록했고(실패 이벤트 유실 방지),
// reportHttp/toMonitorPath 는 호이스팅되어 거기서 참조된다.
// monitor는 fetch로만 전송하므로 이 인터셉터가 모니터링 요청을 다시 잡지 않는다.

function toMonitorPath(url) {
  if (!url) return url;
  const base = getBaseUrl();
  let path = base && url.startsWith(base) ? url.slice(base.length) : url;
  const queryIndex = path.indexOf("?");
  return queryIndex >= 0 ? path.slice(0, queryIndex) : path;
}

function reportHttp(config, status) {
  try {
    if (!config || !monitor.enabled) return;
    const startTime = config.metadata?.startTime;
    const durationMs =
      typeof startTime === "number" ? Date.now() - startTime : undefined;

    // 서버 스키마: { method, route, status, duration_ms, occurred_at, release? }
    // status가 없으면(네트워크 오류) SDK가 599로 정규화한다.
    monitor.captureHttp({
      method: config.method,
      route: toMonitorPath(config.url),
      status,
      duration_ms: durationMs,
    });
  } catch {
    // 모니터링 실패가 앱 흐름을 깨지 않도록 무시한다.
  }
}

axios.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => Promise.reject(error)
);

const send = async ({
  method = "",
  path = "",
  data = {},
  access_token = "",
  headers = {},
} = {}) => {
  const baseUrl = getBaseUrl();
  const url = baseUrl + path;
  assertStaffApiAllowed({ path });

  const token = access_token || getAuthState()?.accessToken || "";
  const defaultHeaders = {
    "content-type": "application/json;charset=UTF-8",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const options = {
    method,
    url,
    headers: { ...defaultHeaders, ...headers },
    data,
    withCredentials: true,
  };

  try {
    const response = await axios(options);
    return response.data;
  } catch (error) {
    console.error("API Error:", error.response || error.message);
    throw error;
  }
};

const getApi = ({ path = "", access_token = "" } = {}) =>
  send({ method: "GET", path, access_token });
const putApi = ({ path = "", data = {}, access_token = "" } = {}) =>
  send({ method: "PUT", path, data, access_token });
const patchApi = ({ path = "", data = {}, access_token = "" } = {}) =>
  send({ method: "PATCH", path, data, access_token });
const postApi = ({ path = "", data = {}, access_token = "" } = {}) =>
  send({ method: "POST", path, data, access_token });
const deleteApi = ({ path = "", data = {}, access_token = "" } = {}) =>
  send({ method: "DELETE", path, data, access_token });

const uploadFileApi = ({ path = "", formData, access_token = "" } = {}) => {
  const baseUrl = getBaseUrl();
  const url = baseUrl + path;
  const token = access_token || getAuthState()?.accessToken || "";
  return axios({
    method: "POST",
    url,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    data: formData,
    withCredentials: true,
  }).then((r) => r.data);
};

export { getApi, putApi, postApi, patchApi, deleteApi, uploadFileApi };
